import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SEOHead } from '../seo';
import { useWordPressPost } from '../../hooks/useWordPress';

function BlogPostPage() {
  const { slug } = useParams();
  const { data: postData, loading, error } = useWordPressPost(slug);
  const [headings, setHeadings] = useState([]);
  const [activeHeading, setActiveHeading] = useState('');
  const [readingProgress, setReadingProgress] = useState(0);
  
  // WordPress returns array, get first post
  const post = postData && Array.isArray(postData) && postData.length > 0 ? postData[0] : null;

  // Function to extract and process headings
  const extractHeadings = (content) => {
    if (!content) return [];
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const h2Elements = doc.querySelectorAll('h2');
    
    const headingList = Array.from(h2Elements).map((heading, index) => {
      const title = heading.textContent || heading.innerText;
      const slug = title.toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '');
      
      return {
        id: index + 1,
        title,
        slug,
      };
    });
    
    return headingList;
  };

  // Function to add IDs to headings in content
  const addHeadingIds = (content, headingsList) => {
    if (!content || !headingsList.length) return content;
    
    let processedContent = content;
    headingsList.forEach((heading) => {
      const regex = new RegExp(`<h2([^>]*)>([^<]*${heading.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^<]*)</h2>`, 'i');
      processedContent = processedContent.replace(regex, `<h2$1 id="${heading.slug}">$2</h2>`);
    });
    
    return processedContent;
  };


  // Transform post data
  const transformedPost = post ? {
    id: post.id,
    title: post.title?.rendered || 'Untitled Post',
    content: post.content?.rendered || 'No content available.',
    excerpt: post.excerpt?.rendered?.replace(/<[^>]*>/g, '') || 'No excerpt available.',
    author: post._embedded?.author?.[0]?.name || 'TherapyList Team',
    authorBio: post._embedded?.author?.[0]?.description || '',
    authorDescription: post._embedded?.author?.[0]?.meta?.description || post._embedded?.author?.[0]?.description || '',
    authorAvatar: null, // Use icon fallback like BlogPage
    authorTitle: post._embedded?.author?.[0]?.meta?.sabox_author_title || post._embedded?.author?.[0]?.meta?.job_title || '',
    authorCompany: post._embedded?.author?.[0]?.meta?.sabox_author_company || post._embedded?.author?.[0]?.meta?.company || '',
    authorWebsite: post._embedded?.author?.[0]?.meta?.sabox_author_web || post._embedded?.author?.[0]?.url || post._embedded?.author?.[0]?.link || '',
    authorTwitter: post._embedded?.author?.[0]?.meta?.sabox_author_twitter || '',
    authorLinkedin: post._embedded?.author?.[0]?.meta?.sabox_author_linkedin || '',
    date: new Date(post.date).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }),
    modifiedDate: new Date(post.modified).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }),
    readTime: Math.max(1, Math.ceil((post.content?.rendered?.replace(/<[^>]*>/g, '').split(' ').length || 0) / 200)) + " min read",
    category: post._embedded?.['wp:term']?.[0]?.[0]?.name || "Mental Health",
    tags: post._embedded?.['wp:term']?.[1] || [],
    image: post._embedded?.['wp:featuredmedia']?.[0]?.source_url || 
           `https://images.unsplash.com/photo-1544027993-37dbfe43562a?w=1200&h=600&fit=crop`,
    slug: post.slug
  } : null;

  // Extract headings and process content when post data is available
  useEffect(() => {
    if (post?.content?.rendered) {
      const extractedHeadings = extractHeadings(post.content.rendered);
      setHeadings(extractedHeadings);
    }
  }, [post?.content?.rendered]);

  // Process content with heading IDs
  const processedContent = transformedPost && headings.length > 0 
    ? addHeadingIds(transformedPost.content, headings)
    : transformedPost?.content;

  // Scroll and reading progress handlers
  useEffect(() => {
    const handleScroll = () => {
      // Reading progress
      const article = document.querySelector('.article-content');
      if (article) {
        const articleTop = article.offsetTop;
        const articleHeight = article.offsetHeight;
        const scrollTop = window.pageYOffset;
        const windowHeight = window.innerHeight;
        
        const progress = Math.min(
          100,
          Math.max(0, ((scrollTop + windowHeight - articleTop) / articleHeight) * 100)
        );
        setReadingProgress(progress);
      }

      // Active heading detection
      if (headings.length > 0) {
        const headingElements = headings.map(h => document.getElementById(h.slug)).filter(Boolean);
        let current = '';
        
        for (let i = headingElements.length - 1; i >= 0; i--) {
          const element = headingElements[i];
          if (element && element.getBoundingClientRect().top <= 150) {
            current = element.id;
            break;
          }
        }
        
        setActiveHeading(current);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [headings]);

  // Smooth scroll to heading
  const scrollToHeading = (slug) => {
    const element = document.getElementById(slug);
    if (element) {
      const headerOffset = 120;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // SEO data
  const seoProps = transformedPost ? {
    title: transformedPost.title + ' - TherapyList Blog',
    description: transformedPost.excerpt.substring(0, 155),
    canonical: `/blog/${transformedPost.slug}`,
    ogTitle: transformedPost.title,
    ogDescription: transformedPost.excerpt.substring(0, 155),
    ogImage: transformedPost.image,
    type: 'article'
  } : {};

  if (loading) {
    return (
      <>
        <SEOHead {...seoProps} />
        <div className="min-h-screen bg-[#e4fde1] flex items-center justify-center" style={{ marginTop: '-200px', paddingTop: '200px' }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#254e70] mx-auto mb-4"></div>
            <p className="text-[#254e70]" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>Loading article...</p>
          </div>
        </div>
      </>
    );
  }

  if (error || !transformedPost) {
    return (
      <>
        <SEOHead {...seoProps} />
        <div className="min-h-screen bg-[#e4fde1] flex items-center justify-center" style={{ marginTop: '-200px', paddingTop: '200px' }}>
          <div className="text-center">
            <h1 className="text-3xl font-medium text-[#254e70] mb-4" style={{ fontFamily: 'Kanit, sans-serif' }}>Article Not Found</h1>
            <p className="text-[#254e70]/70 mb-6" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
              Sorry, we couldn't find the article you're looking for.
            </p>
            <Link 
              to="/blog" 
              className="bg-[#254e70] text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-transform"
              style={{ fontFamily: 'Nunito Sans, sans-serif' }}
            >
              Back to Blog
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead {...seoProps} />
      
      <div className="min-h-screen bg-[#e4fde1]" style={{ marginTop: '-200px', paddingTop: '200px' }}>
        {/* Breadcrumbs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <nav className="flex items-center space-x-2 text-sm text-[#254e70]/60 mb-8" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
            <Link to="/" className="hover:text-[#254e70] transition-colors">Home</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-[#254e70] transition-colors">Blog</Link>
            <span>/</span>
            <span className="text-[#254e70]">{transformedPost.title}</span>
          </nav>
        </div>

        {/* Featured Image */}
        {transformedPost.image && (
          <div className="relative h-80 w-full">
            <img 
              src={transformedPost.image} 
              alt={transformedPost.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#254e70]/60 to-[#254e70]/40"></div>
          </div>
        )}

        {/* Main Content Container */}
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${transformedPost.image ? '-mt-40' : 'pt-8'} pb-8 relative z-10`}>

          {/* Content Layout */}
          <div className="flex flex-col lg:flex-row gap-8">
            
            {/* Table of Contents Sidebar */}
            {headings.length > 0 && (
              <div className="lg:w-80 flex-shrink-0 order-2 lg:order-1">
                <div className="sticky top-36">
                  <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                    {/* ToC Header */}
                    <div className="bg-gradient-to-r from-[#254e70] to-[#1d3e5a] px-6 py-4">
                      <h2 className="text-sm font-semibold text-white uppercase tracking-wide flex items-center gap-2" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h7" />
                        </svg>
                        Table of Contents
                      </h2>
                    </div>

                    {/* ToC Links */}
                    <nav className="p-6">
                      <div className="space-y-3">
                        {headings.map((heading) => (
                          <button
                            key={heading.slug}
                            onClick={() => scrollToHeading(heading.slug)}
                            className={`group flex items-center gap-3 w-full text-left transition-colors ${
                              activeHeading === heading.slug 
                                ? 'text-[#254e70]' 
                                : 'text-[#254e70]/60 hover:text-[#254e70]'
                            }`}
                          >
                            <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                              activeHeading === heading.slug
                                ? 'bg-[#9dacff]/20 text-[#254e70]'
                                : 'bg-[#e4fde1] text-[#254e70]/60 group-hover:bg-[#9dacff]/10 group-hover:text-[#254e70]'
                            }`}>
                              {heading.id}
                            </span>
                            <span className="text-sm font-medium" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                              {heading.title}
                            </span>
                          </button>
                        ))}
                      </div>
                    </nav>

                    {/* Progress Bar */}
                    <div className="px-6 py-3 bg-[#e4fde1]/30 border-t border-[#9dacff]/10">
                      <div className="text-xs text-[#254e70]/60 flex items-center gap-2" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                        </svg>
                        Reading progress
                        <div className="flex-1 h-1 bg-[#9dacff]/20 rounded-full">
                          <div 
                            className="h-1 bg-[#254e70] rounded-full transition-all duration-300"
                            style={{ width: `${readingProgress}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="p-6 border-t border-[#9dacff]/10">
                      <div className="text-center">
                        <h3 className="text-sm font-semibold text-[#254e70] mb-2" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                          Ready to get started?
                        </h3>
                        <p className="text-sm text-[#254e70]/70 mb-4" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                          Find your perfect therapist match today.
                        </p>
                        <Link 
                          to="/find-therapists"
                          className="block w-full bg-gradient-to-r from-[#254e70] to-[#1d3e5a] text-white px-4 py-2 rounded-xl text-sm font-medium hover:scale-105 transition-transform"
                          style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                        >
                          Find Therapists
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Main Content Area */}
            <div className={`flex-1 order-1 lg:order-2 ${headings.length === 0 ? 'max-w-4xl mx-auto' : ''}`}>
              {/* Article Header & Content Combined */}
              <div className="bg-white rounded-2xl shadow-xl p-10 lg:p-12 mb-8">
                {/* Article Header */}
                <div className="flex flex-wrap items-center gap-4 mb-6">
                  <span className="bg-[#9dacff]/10 text-[#254e70] px-4 py-2 rounded-full text-sm font-medium" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                    {transformedPost.category}
                  </span>
                  <span className="text-[#254e70]/60 text-sm" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                    {transformedPost.readTime} • {transformedPost.date}
                  </span>
                </div>
                
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-medium text-[#254e70] mb-8 leading-tight" style={{ fontFamily: 'Kanit, sans-serif' }}>
                  {transformedPost.title}
                </h1>
                {/* Article Content */}
                <div 
                  className="article-content prose prose-base max-w-none text-[#254e70] leading-relaxed px-2
                    prose-headings:text-[#254e70] prose-headings:font-medium
                    prose-p:text-[#254e70]/90 prose-p:leading-7
                    prose-a:text-[#254e70] prose-a:font-semibold prose-a:underline prose-a:decoration-2 prose-a:underline-offset-2 prose-a:decoration-[#9dacff]/50 
                    hover:prose-a:decoration-[#254e70] hover:prose-a:text-[#1d3e5a]
                    prose-strong:text-[#254e70] prose-strong:font-semibold
                    prose-ul:text-[#254e70]/90 prose-ol:text-[#254e70]/90
                    prose-li:text-[#254e70]/90 prose-li:leading-7 prose-li:mb-2
                    prose-blockquote:text-[#254e70]/80 prose-blockquote:border-l-[#9dacff] prose-blockquote:bg-[#e4fde1]/30 prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:rounded-r-lg
                    prose-code:text-[#254e70] prose-code:bg-[#e4fde1]/50 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:font-mono prose-code:text-sm
                    prose-pre:bg-[#254e70] prose-pre:text-white
                    prose-hr:border-[#9dacff]/30"
                  style={{ 
                    fontFamily: 'Nunito Sans, sans-serif',
                    fontSize: '16px'
                  }}
                  dangerouslySetInnerHTML={{ __html: processedContent }}
                />

                {/* Tags */}
                {transformedPost.tags.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-[#9dacff]/20">
                    <div className="flex flex-wrap gap-2">
                      {transformedPost.tags.map((tag) => (
                        <span 
                          key={tag.id}
                          className="bg-[#9dacff]/10 text-[#254e70] px-3 py-1 rounded-full text-sm"
                          style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                        >
                          #{tag.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>


              {/* Author Card - Simple Author Box Plugin Data */}
              <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
                <div className="flex items-start gap-6">
                  {/* Author Avatar - Light Blue Circle */}
                  <div className="w-20 h-20 bg-[#9dacff] rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  </div>
                  
                  <div className="flex-1">
                    {/* Author Name */}
                    <h3 className="text-xl font-semibold text-[#254e70] mb-3" style={{ fontFamily: 'Kanit, sans-serif' }}>
                      About {transformedPost.author}
                    </h3>
                    
                    {/* Author Bio */}
                    <div 
                      className="author-bio-content text-[#254e70]/80 leading-7 mb-4"
                      style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '16px' }}
                      dangerouslySetInnerHTML={{ 
                        __html: transformedPost.authorBio || transformedPost.authorDescription || `${transformedPost.author} is a contributor to the TherapyList blog, sharing insights and expertise in mental health and wellness.`
                      }}
                    />
                    
                    {/* Author Details */}
                    <div className="flex flex-wrap gap-4 text-[#254e70]/60" style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '16px' }}>
                      {transformedPost.authorTitle && (
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                          </svg>
                          {transformedPost.authorTitle}
                        </span>
                      )}
                      
                      {transformedPost.authorCompany && (
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          {transformedPost.authorCompany}
                        </span>
                      )}
                    </div>

                    {/* Author Social Links */}
                    {(transformedPost.authorTwitter || transformedPost.authorLinkedin) && (
                      <div className="flex gap-3 mt-4">
                        {transformedPost.authorTwitter && (
                          <a 
                            href={`https://twitter.com/${transformedPost.authorTwitter.replace('@', '')}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-[#254e70]/60 hover:text-[#254e70] transition-colors"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                            </svg>
                          </a>
                        )}
                        
                        {transformedPost.authorLinkedin && (
                          <a 
                            href={transformedPost.authorLinkedin} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-[#254e70]/60 hover:text-[#254e70] transition-colors"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                            </svg>
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <Link 
                  to="/blog" 
                  className="inline-flex items-center gap-2 bg-[#254e70] text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-transform"
                  style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                  Back to Blog
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default BlogPostPage;
